/**
 * Reports > GL Exports (/reports/gl-exports).
 *
 * Pick a fiscal month, check each journal's row count and debit and credit totals, and
 * download the Sales journal or the Inventory journal as an Excel file. Replaces the
 * QuickSight dashboards "GL_SALES_MASTER" and "GL_INV_MASTER". There is no table of
 * journal lines on screen, because the files carry them.
 *
 * Thin page: the screen is the shared `GlExports` component in `@ampersandbrands/ui`
 * (presentational); this file fetches through `useGlExports`, keeps the chosen fiscal
 * month in the URL, and adds the page chrome.
 *
 * RBAC: head office only, `reports.glexports`. No store-manager view.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GlExports as GlExportsScreen, type GlJournalKey } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { downloadFinanceXlsx, parseGlMonth, useGlExports, writeGlMonth } from '@/lib/useFinance';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. Head office only. */
const SLUG = 'reports.glexports';

function GlExportsInner() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlMonth = useMemo(() => parseGlMonth(searchParams), [searchParams]);
  const [downloading, setDownloading] = useState<GlJournalKey | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dataQ = useGlExports(urlMonth, canView);
  const selected = dataQ.selected;

  const select = useCallback(
    (fiscalYear: number, fiscalMonth: number) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeGlMonth(sp, { fiscal_year: fiscalYear, fiscal_month: fiscalMonth });
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const pageState = useMemo(() => [{ label: 'Fiscal month', value: selected ? selected.label : 'Newest complete month' }], [selected]);
  useReportPageState(pageState);

  async function handleDownload(journal: GlJournalKey) {
    if (!selected) return;
    setDownloading(journal);
    setToast(null);
    try {
      const qs = new URLSearchParams({
        journal,
        fiscal_year: String(selected.fiscal_year),
        fiscal_month: String(selected.fiscal_month),
      }).toString();
      await downloadFinanceXlsx('gl-exports/download', qs, `gl-${journal}.xlsx`);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="GL Exports" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="GL Exports"
      subtitle="Download the Sales journal or the Inventory journal for a fiscal month as an Excel file, with the same columns as before. Check the line count and the debit and credit totals first. Refreshed from CounterPoint once a day."
      actions={<ToolPinToggle toolSlug="gl-exports" toolName="GL Exports" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="GL Exports" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <GlExportsScreen
        periods={dataQ.periods}
        selected={selected}
        journals={dataQ.journals}
        onSelect={select}
        onDownload={(j) => void handleDownload(j)}
        downloading={downloading}
        asOf={dataQ.asOf}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
      />
    </div>
  );
}

export function GlExports() {
  return (
    <ErrorBoundary>
      <GlExportsInner />
    </ErrorBoundary>
  );
}
